import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  CheckCircle,
  XCircle,
  Question,
  Clock,
  Eye,
  Package,
  Spinner,
  ArrowLeft,
  ArrowsClockwise,
  ChartBar
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = {
  USD: '$',
  XCD: 'EC$',
  JMD: 'J$',
  TTD: 'TT$'
};

const FCApprovalQueuePage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [pendingPOs, setPendingPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPO, setSelectedPO] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingPOs();
  }, []);

  const fetchPendingPOs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/approval-queue`, getAuthHeaders());
      setPendingPOs(res.data);
    } catch (error) {
      console.error('Failed to load approval queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedPO || !actionDialog.type) return;
    
    if ((actionDialog.type === 'reject' || actionDialog.type === 'query') && !comment.trim()) {
      toast.error(`Please provide a ${actionDialog.type === 'reject' ? 'rejection reason' : 'query message'}`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/purchase-orders/${selectedPO.id}/approve`,
        { action: actionDialog.type, comment: comment.trim() || null },
        getAuthHeaders()
      );
      
      const messages = {
        approve: 'PO approved and issued to vendor',
        reject: 'PO rejected',
        query: 'Query sent to Purchasing Manager'
      };
      
      toast.success(messages[actionDialog.type]);
      setActionDialog({ open: false, type: null });
      setComment('');
      setSelectedPO(null);
      await fetchPendingPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionDialog.type} PO`);
    } finally {
      setSubmitting(false);
    }
  };

  const openAction = (type) => {
    setActionDialog({ open: true, type });
    setComment('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-[#0F52BA]" />
      </div>
    );
  }

  // Detail View for Pending PO
  if (selectedPO) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedPO(null)} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedPO.po_number}
            </h1>
            <p className="text-slate-500">PO Approval Review</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-4 bg-white rounded-xl border border-slate-200">
          <Button
            onClick={() => openAction('approve')}
            className="bg-[#2D6A4F] hover:bg-[#1B4D3E] flex-1"
            data-testid="approve-po-btn"
          >
            <CheckCircle size={18} className="mr-2" />
            Approve
          </Button>
          <Button
            onClick={() => openAction('reject')}
            variant="outline"
            className="border-[#E63946] text-[#E63946] hover:bg-[#E63946]/10 flex-1"
            data-testid="reject-po-btn"
          >
            <XCircle size={18} className="mr-2" />
            Reject
          </Button>
          <Button
            onClick={() => openAction('query')}
            variant="outline"
            className="flex-1"
            data-testid="query-po-btn"
          >
            <Question size={18} className="mr-2" />
            Query
          </Button>
        </div>

        {/* PO Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Total Value</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">
              {CURRENCY_SYMBOLS[selectedPO.currency]}{selectedPO.total_value.toLocaleString()}
            </p>
            {selectedPO.total_value >= 2000 && (
              <span className="text-xs text-[#E63946] font-medium">High Value Order</span>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Vendor</p>
            <p className="text-lg font-semibold text-slate-800 mt-1">{selectedPO.vendor_name}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Department</p>
            <p className="text-lg font-semibold text-slate-800 mt-1">{selectedPO.department || 'General'}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Requested By</p>
            <p className="text-lg font-semibold text-slate-800 mt-1">{selectedPO.created_by_name || 'N/A'}</p>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Line Items</h2>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Item</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Unit Price</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {selectedPO.line_items?.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{item.item_name}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{item.quantity} {item.unit}</td>
                  <td className="px-4 py-3 text-right text-slate-600">
                    {CURRENCY_SYMBOLS[selectedPO.currency]}{item.unit_price.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-800">
                    {CURRENCY_SYMBOLS[selectedPO.currency]}{item.total_price.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right font-semibold text-slate-800">Total:</td>
                <td className="px-4 py-3 text-right font-bold text-[#0F52BA]">
                  {CURRENCY_SYMBOLS[selectedPO.currency]}{selectedPO.total_value.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Action Dialog */}
        <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {actionDialog.type === 'approve' && <CheckCircle className="text-[#2D6A4F]" />}
                {actionDialog.type === 'reject' && <XCircle className="text-[#E63946]" />}
                {actionDialog.type === 'query' && <Question className="text-[#0F52BA]" />}
                {actionDialog.type === 'approve' && 'Approve Purchase Order'}
                {actionDialog.type === 'reject' && 'Reject Purchase Order'}
                {actionDialog.type === 'query' && 'Query Purchase Order'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {actionDialog.type === 'approve' ? (
                <p className="text-slate-600">
                  Confirm approval of <strong>{selectedPO?.po_number}</strong> for{' '}
                  <strong>{CURRENCY_SYMBOLS[selectedPO?.currency]}{selectedPO?.total_value.toLocaleString()}</strong>?
                  <br /><br />
                  This will issue the PO to the vendor.
                </p>
              ) : (
                <div>
                  <Label>{actionDialog.type === 'reject' ? 'Rejection Reason *' : 'Query Message *'}</Label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={actionDialog.type === 'reject' ? 'Enter reason for rejection...' : 'Enter your query...'}
                    rows={3}
                    className="mt-1"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null })}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAction}
                  disabled={submitting}
                  className={
                    actionDialog.type === 'approve' ? 'bg-[#2D6A4F] hover:bg-[#1B4D3E]' :
                    actionDialog.type === 'reject' ? 'bg-[#E63946] hover:bg-[#C62D3A]' :
                    'bg-[#0F52BA] hover:bg-[#0A3D8F]'
                  }
                >
                  {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                  {actionDialog.type === 'approve' && 'Approve'}
                  {actionDialog.type === 'reject' && 'Reject'}
                  {actionDialog.type === 'query' && 'Send Query'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Dashboard View - FC Dashboard shows ONLY pending financial approvals
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Financial Controller Dashboard
          </h1>
          <p className="text-slate-500">POs Pending Your Approval</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPendingPOs} disabled={loading} data-testid="refresh-btn">
          <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Quick Stat - Pending Approvals Only */}
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock size={24} weight="fill" className="text-amber-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-amber-600" data-testid="pending-approvals-count">{pendingPOs.length}</p>
              <p className="text-sm text-slate-500">POs Pending Your Approval</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Approvals Section - ONLY content on FC dashboard */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="pending-approvals-section">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">POs Pending Financial Approval</h2>
          <p className="text-sm text-slate-500">Review and approve purchase orders requiring your authorization</p>
        </div>
        
        {pendingPOs.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle size={48} className="mx-auto text-green-400 mb-3" />
            <p className="text-slate-600 font-medium">All caught up!</p>
            <p className="text-sm text-slate-500">No purchase orders pending your approval</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {pendingPOs.map(po => (
              <div key={po.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`pending-po-${po.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#0F52BA]/10 flex items-center justify-center">
                      <Package size={24} className="text-[#0F52BA]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{po.po_number}</p>
                      <p className="text-sm text-slate-500">{po.vendor_name}</p>
                      <p className="text-xs text-slate-400">{po.department || 'General'} • {format(new Date(po.created_at), 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-4">
                    <div>
                      <p className="text-lg font-bold text-slate-800">
                        {CURRENCY_SYMBOLS[po.currency]}{po.total_value.toLocaleString()}
                      </p>
                      {po.total_value >= 2000 && (
                        <span className="text-xs text-amber-600 font-medium">High Value</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setSelectedPO(po)}
                      className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
                      data-testid={`review-po-${po.id}`}
                    >
                      <Eye size={16} className="mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reports Card - At the bottom of FC Dashboard */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="reports-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center">
            <ChartBar size={28} className="text-[#3D1D72]" weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800">Reports</h3>
            <p className="text-sm text-slate-500">Access Month End Inventory, Receiving, Variance, and Spend by Department reports</p>
          </div>
          <Button 
            onClick={() => window.location.href = '/reports'}
            className="bg-[#3D1D72] hover:bg-[#2D1552]"
            data-testid="open-reports-btn"
          >
            Open Reports
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FCApprovalQueuePage;
